import { useState, useEffect } from 'react';
import { backendUrl } from 'config/envConfig';
import { reportConfigLoadError } from './configObservability';

const CONFIG_LOAD_TIMEOUT_MS = 10000;
const BOOTSTRAP_CACHE_KEY = 'dashboard_bootstrap';

const fetchWithTimeout = (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  );
};

/**
 * Post-login hook: fetches full tenant config (bootstrap) from BFF.
 * Requires auth token. Falls back to localStorage cache on failure.
 */
const useBootstrap = (token) => {
  const [bootstrap, setBootstrap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const response = await fetchWithTimeout(
          `${backendUrl}/api/v1/tenant/bootstrap`,
          {
            headers: {
              'X-Authentication-Token': token,
            },
            credentials: 'include',
          },
          CONFIG_LOAD_TIMEOUT_MS,
        );

        if (!response.ok) {
          throw new Error(`bootstrap responded ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        setBootstrap(data);
        localStorage.setItem(
          BOOTSTRAP_CACHE_KEY,
          JSON.stringify({ ...data, cachedAt: Date.now() }),
        );
      } catch (err) {
        console.error('Bootstrap fetch failed, attempting fallback', err);
        reportConfigLoadError('bootstrap', err);

        if (cancelled) return;

        const cached = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
        if (cached) {
          try {
            setBootstrap(JSON.parse(cached));
          } catch {
            setError(err);
          }
        } else {
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { bootstrap, error, loading };
};

export default useBootstrap;
